import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Avatar,
    Paper,
    IconButton,
    Tooltip,
    Fade,
    Grow,
    CircularProgress
} from '@mui/material';
import {
    Send as SendIcon,
    SmartToy as RobotIcon,
    Person as PersonIcon,
    CheckCircle as CheckCircleIcon,
    Event as CalendarIcon,
    Article as CaseStudyIcon,
    ArrowForward as ArrowIcon,
    Mic as MicIcon,
    Stop as StopIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const VerenaChat = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [step, setStep] = useState('welcome');
    const [isTyping, setIsTyping] = useState(false);
    const [leadData, setLeadData] = useState({
        name: '',
        email: '',
        businessType: '',
        problem: '',
        goal: ''
    });
    const [isRecording, setIsRecording] = useState(false);
    const recognitionRef = useRef(null);

    const textBeforeRecording = useRef('');

    const handleVoiceInput = () => {
        if (isRecording) {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
            setIsRecording(false);
            return;
        }

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            toast.warn("Your browser does not support voice input. Please use Chrome or Edge.");
            return;
        }

        // Save current text so we append to it
        textBeforeRecording.current = input;

        const recognition = new SpeechRecognition();
        recognitionRef.current = recognition;

        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
            setIsRecording(true);
        };

        recognition.onresult = (event) => {
            let transcript = '';
            // When continuous=false, we usually get the full transcript of the utterance in results[0]
            // We iterate just in case, but for single utterance, we want the whole thing.
            for (let i = 0; i < event.results.length; ++i) {
                transcript += event.results[i][0].transcript;
            }

            // Smart spacing
            const prefix = textBeforeRecording.current.trim();
            const spacer = prefix ? ' ' : '';
            setInput(prefix + spacer + transcript);
        };

        recognition.onerror = (event) => {
            console.error("Speech recognition error", event.error);
            setIsRecording(false);
        };

        recognition.onend = () => {
            setIsRecording(false);
        };

        recognition.start();
    };
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    const hasStarted = useRef(false);

    useEffect(() => {
        // Initial welcome message
        if (!hasStarted.current) {
            hasStarted.current = true;
            startConversation();
        }
    }, []);

    const addMessage = (role, content) => {
        setMessages(prev => [...prev, { role, content, id: Date.now() }]);
    };

    const simulateTyping = async (text, duration = 1500) => {
        setIsTyping(true);
        await new Promise(resolve => setTimeout(resolve, duration));
        setIsTyping(false);
        addMessage('assistant', text);
    };

    const startConversation = async () => {
        await simulateTyping("Hi! I'm Verena, your AI Sales Consultant from VerveAI. I'm here to help you explore how automation and AI can scale your business.");
        await simulateTyping("To get started, what should I call you?");
        setStep('name');
    };

    const handleSend = async (e) => {
        if (e) e.preventDefault();
        if (!input.trim()) return;

        const userMessage = input.trim();
        setInput('');
        addMessage('user', userMessage);

        processStep(userMessage);
    };

    const processStep = async (message) => {
        switch (step) {
            case 'name':
                setLeadData(prev => ({ ...prev, name: message }));
                await simulateTyping(`Nice to meet you, ${message}! What's your professional email address so I can send you our custom diagnosis later?`);
                setStep('email');
                break;

            case 'email':
                if (!/^\S+@\S+\.\S+$/.test(message)) {
                    await simulateTyping("That doesn't look like a valid email. Could you double-check it for me?");
                    return;
                }
                setLeadData(prev => ({ ...prev, email: message }));
                await simulateTyping("Got it. Tell me a bit about your business or the project you're working on. What do you do?");
                setStep('business');
                break;

            case 'business':
                setLeadData(prev => ({ ...prev, businessType: message }));
                await simulateTyping("Interesting! And what's the biggest manual bottleneck or 'pain point' you're facing right now in your operations?");
                setStep('problem');
                break;

            case 'problem':
                setLeadData(prev => ({ ...prev, problem: message }));
                await simulateTyping("I see. If you could automate one major outcome over the next 30 days, what would success look like for you?");
                setStep('goal');
                break;

            case 'goal':
                setLeadData(prev => ({ ...prev, goal: message }));
                setStep('analyzing');
                await simulateTyping("Thank you for sharing all of that. Give me a moment while I analyze your requirements against our AI automation frameworks...");

                // Artificial analysis delay
                setIsTyping(true);
                await new Promise(resolve => setTimeout(resolve, 3000));
                setIsTyping(false);

                provideFinalRecommendation(message);
                break;

            default:
                break;
        }
    };

    // Helper to get the correct API URL based on environment
    const getApiUrl = (functionName) => {
        const baseUrl = import.meta.env.VITE_FUNCTIONS_BASE_URL;
        if (baseUrl) {
            return `${baseUrl.replace(/\/$/, '')}/${functionName}`;
        }
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            return `http://127.0.0.1:5001/verveailabs/us-central1/${functionName}`;
        }
        return `https://us-central1-verveailabs.cloudfunctions.net/${functionName}`;
    };

    const provideFinalRecommendation = async () => {
        try {
            // Call the backend Verena Agent for a real diagnosis
            const response = await fetch(getApiUrl('verenaAgent'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sessionId: `sess_${Date.now()}`,
                    userMessage: `Diagnose for ${leadData.name}`,
                    context: {
                        phase: 'diagnosis',
                        leadData
                    }
                })
            });

            if (!response.ok) throw new Error('Diagnosis failed');

            const result = await response.json();

            await simulateTyping(result.message, 2500);

            // Direct submission flow instead of Calendly
            await simulateTyping("I've prepared a detailed strategy for you. Should I send this directly to your team at VerveAI for a closer look?");
            setStep('result_submission');

        } catch (error) {
            console.error('Verena API Error:', error);
            // Fallback to local logic if backend fails
            await simulateTyping(`Based on my analysis, ${leadData.name}, your business is prime for high-impact automation. I've mapped out a potential solution that could save you 20+ hours a week.`);
            await simulateTyping("Should I send this directly to our team so they can reach out to you with specific next steps?");
            setStep('result_submission');
        }
    };

    const submitLead = async () => {
        try {
            const response = await fetch(getApiUrl('submitContactForm'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data: leadData })
            });

            if (!response.ok) throw new Error('Submission failed');

            await simulateTyping("Perfect! I've sent your details to our team. They'll review your case and get back to you shortly.");
            setStep('finished');
        } catch (error) {
            console.error('Submission Error:', error);
            await simulateTyping("It seems I'm having trouble connecting to the server. But don't worry, you can email us directly at contact@verveai.co.");
            setStep('finished');
        }
    };


    return (
        <Box sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
        }}>
            {/* Chat Messages Area */}
            <Box sx={{
                flexGrow: 1,
                overflowY: 'auto',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '10px' }
            }}>
                {messages.map((msg) => (
                    <Fade in key={msg.id} timeout={500}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: msg.role === 'assistant' ? 'row' : 'row-reverse',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            mb: 1
                        }}>
                            <Avatar sx={{
                                bgcolor: msg.role === 'assistant' ? 'primary.main' : 'rgba(255,255,255,0.1)',
                                width: 36,
                                height: 36,
                                border: msg.role === 'assistant' ? '2px solid rgba(255,107,53,0.3)' : 'none'
                            }}>
                                {msg.role === 'assistant' ? <RobotIcon sx={{ fontSize: 20 }} /> : <PersonIcon sx={{ fontSize: 20 }} />}
                            </Avatar>
                            <Paper sx={{
                                p: 2,
                                maxWidth: '80%',
                                borderRadius: msg.role === 'assistant' ? '2px 20px 20px 20px' : '20px 2px 20px 20px',
                                bgcolor: msg.role === 'assistant' ? 'rgba(255,255,255,0.03)' : 'primary.main',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.05)',
                                boxShadow: msg.role === 'assistant' ? 'none' : '0 10px 20px rgba(0,122,255,0.2)'
                            }}>
                                <Typography variant="body1" sx={{
                                    color: msg.role === 'assistant' ? 'text.primary' : '#fff',
                                    lineHeight: 1.6,
                                    fontSize: '0.95rem'
                                }}>
                                    {msg.content}
                                </Typography>
                            </Paper>
                        </Box>
                    </Fade>
                ))}
                {isTyping && (
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                            <RobotIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Paper sx={{ p: 2, borderRadius: '2px 20px 20px 20px', bgcolor: 'rgba(255,255,255,0.03)' }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Box className="typing-dot" sx={{ width: 6, height: 6, bgcolor: 'text.secondary', borderRadius: '50%', animation: 'bounce 1s infinite' }} />
                                <Box className="typing-dot" sx={{ width: 6, height: 6, bgcolor: 'text.secondary', borderRadius: '50%', animation: 'bounce 1s infinite 0.2s' }} />
                                <Box className="typing-dot" sx={{ width: 6, height: 6, bgcolor: 'text.secondary', borderRadius: '50%', animation: 'bounce 1s infinite 0.4s' }} />
                            </Box>
                        </Paper>
                    </Box>
                )}
                <div ref={messagesEndRef} />
            </Box>

            {/* Action Buttons for Result Phase */}
            {(step === 'result_submission') && !isTyping && (
                <Grow in timeout={1000}>
                    <Box sx={{ p: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<SendIcon />}
                            onClick={submitLead}
                            sx={{
                                px: 4,
                                py: 1.5,
                                borderRadius: '12px',
                                fontWeight: 'bold',
                                boxShadow: '0 0 20px rgba(0,122,255,0.4)',
                                '&:hover': { transform: 'translateY(-2px)', transition: '0.3s' }
                            }}
                        >
                            Yes, Send Request
                        </Button>
                        <Button
                            sx={{ color: 'text.secondary' }}
                            onClick={() => {
                                addMessage('assistant', "No problem! Feel free to reach out whenever you're ready to scale.");
                                setStep('finished');
                            }}
                        >
                            No, Thanks
                        </Button>
                    </Box>
                </Grow>
            )}

            {/* Input Area */}
            {['name', 'email', 'business', 'problem', 'goal'].includes(step) && (
                <Fade in timeout={500}>
                    <Box component="form" onSubmit={handleSend} sx={{
                        p: 3,
                        bgcolor: 'rgba(0,0,0,0.2)',
                        backdropFilter: 'blur(10px)',
                        borderTop: '1px solid rgba(255,255,255,0.05)',
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center'
                    }}>
                        <Tooltip title={isRecording ? "Stop Listening" : "Speak Answer"}>
                            <IconButton
                                onClick={handleVoiceInput}
                                disabled={isTyping}
                                sx={{
                                    bgcolor: isRecording ? 'error.main' : 'rgba(255,255,255,0.05)',
                                    color: '#fff',
                                    width: 56,
                                    height: 56,
                                    borderRadius: '16px',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    transition: 'all 0.2s',
                                    animation: isRecording ? 'pulse 1.5s infinite' : 'none',
                                    '&:hover': {
                                        bgcolor: isRecording ? 'error.dark' : 'rgba(255,255,255,0.1)',
                                        transform: 'scale(1.05)'
                                    }
                                }}
                            >
                                {isRecording ? <StopIcon /> : <MicIcon />}
                            </IconButton>
                        </Tooltip>

                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder={isRecording ? "Listening..." : (isTyping ? "Verena is typing..." : "Type or speak your answer...")}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            autoFocus
                            disabled={isTyping}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '16px',
                                    bgcolor: 'rgba(255,255,255,0.05)',
                                    color: '#fff',
                                    '& fieldset': { border: '1px solid rgba(255,255,255,0.1)' },
                                    '&:hover fieldset': { borderColor: 'primary.main' },
                                    '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                }
                            }}
                        />
                        <IconButton
                            type="submit"
                            color="primary"
                            disabled={isTyping || !input.trim()}
                            sx={{
                                bgcolor: 'primary.main',
                                color: '#fff',
                                width: 56,
                                height: 56,
                                borderRadius: '16px',
                                transition: '0.2s',
                                '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.05)' },
                                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' }
                            }}
                        >
                            <SendIcon />
                        </IconButton>
                    </Box>
                </Fade>
            )}

            {/* Styles for animation */}
            <style>{`
                @keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-4px); }
                }
                @keyframes pulse {
                    0% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(244, 67, 54, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
                }
            `}</style>
        </Box>
    );
};

export default VerenaChat;
